import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import '../styles/Header.css';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/group-companies', label: t('nav.group_companies') },
    { path: '/products', label: t('nav.products') },
    { path: '/references', label: t('nav.references') },
    { path: '/exports', label: t('nav.exports') },
    { path: '/contact', label: t('nav.contact') }
  ];

  const headerClass = `header ${isScrolled ? 'scrolled' : ''}`;

  return (
    <header className={headerClass}>
      <div className="top-banner">
        <div className="container top-banner-container">
          <div className="top-contact">
            <a href="mailto:info@kusgrup.com.tr">
              <i className="fa-solid fa-envelope"></i>
              info@kusgrup.com.tr
            </a>
            <a href="tel:+905496935858">
              <i className="fa-solid fa-phone"></i>
              +90 549 693 58 58
            </a>
          </div>

          <div className="top-actions">
            <div className="top-socials" aria-label="Sosyal medya bağlantıları">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <i className="fa-brands fa-facebook-f"></i>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <i className="fa-brands fa-instagram"></i>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <i className="fa-brands fa-linkedin-in"></i>
              </a>
            </div>

            <div className="top-lang-switcher" aria-label="Dil seçenekleri">
              <button
                onClick={() => changeLanguage('tr')}
                className={i18n.language === 'tr' ? 'active' : ''}
              >
                <span className="flag flag-tr" aria-hidden="true"></span>
                TR
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className={i18n.language === 'en' ? 'active' : ''}
              >
                <span className="flag flag-en" aria-hidden="true"></span>
                EN
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="header-main">
        <div className="container header-container">
          <Link to="/" className="logo-link">
            <img
              src="/images/1-1.png"
              alt="KUS GRUP Logo"
              className="header-logo"
            />
            <span className="logo-divider" aria-hidden="true"></span>
            <img
              src="/100yil.png"
              alt="TOC Logo"
              className="header-partner-logo"
            />
          </Link>

          <div className="nav-container">
            <nav className="desktop-nav">
              <ul className="nav-links">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className={location.pathname === link.path ? 'active' : ''}
                    >
                      {link.label}
                      {location.pathname === link.path && (
                        <motion.div
                          className="active-indicator"
                          layoutId="activeIndicator"
                        />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <button
              className={`mobile-menu-toggle ${isMobileMenuOpen ? 'open' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>

          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                className="mobile-menu"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mobile-menu-logo">
                  <img src="/images/1-1.png" alt="KUS GRUP Logo" />
                  <img src="/images/toc-logo.png" alt="TOC Logo" />
                </div>
                <ul className="mobile-nav-links">
                  {navLinks.map((link) => (
                    <motion.li
                      key={link.path}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * navLinks.indexOf(link) }}
                    >
                      <Link
                        to={link.path}
                        className={location.pathname === link.path ? 'active' : ''}
                      >
                        {link.label}
                      </Link>
                    </motion.li>
                  ))}
                  <li className="mobile-lang-switcher">
                    <button onClick={() => changeLanguage('tr')} className={i18n.language === 'tr' ? 'active' : ''}>
                      <span className="flag flag-tr" aria-hidden="true"></span> TR
                    </button>
                    <button onClick={() => changeLanguage('en')} className={i18n.language === 'en' ? 'active' : ''}>
                      <span className="flag flag-en" aria-hidden="true"></span> EN
                    </button>
                  </li>
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default Header; 
